import { AnimatePresence, motion, MotionProps } from 'framer-motion'
import cn from 'classnames'
import React, { FocusEventHandler, forwardRef, Ref, useMemo } from 'react'
import Icon from 'core/components/icons/Icon'
import { IconType } from 'core/components/icons/types'

interface SwitchProps
  extends Omit<
    React.InputHTMLAttributes<HTMLInputElement>,
    'onChange' | 'checked' | 'value'
  > {
  status?: 'idle' | 'success' | 'error' | 'loading'
  motionProps?: MotionProps
  checkedIcon?: `${IconType}` | React.ReactElement
  uncheckedIcon?: `${IconType}` | React.ReactElement
  onChange?: (checked: boolean) => void
  value?: boolean
}

/**
 *
 * @description: A switch component that implements a fancy checkbox. Based on top of native html input component.
 * Note: This is a controlled component.
 * Note: There are magic code that capture css classNames inside.
 * Note: Does not support indeterminate state.
 */
const Switch = forwardRef(
  (
    {
      className,
      status = 'idle',
      motionProps = {
        variants: {
          on: {
            scale: [1, 1.1, 1]
          },
          off: {
            scale: [1, 1.1, 1]
          },
          indeterminate: {
            scale: [1, 1.1, 1]
          }
        }
      },
      value,
      disabled = false,
      onChange = console.log,
      onBlur = console.log,
      ...props
    }: SwitchProps,
    ref: Ref<HTMLInputElement>
  ) => {
    // magic code
    // since we are hiding the underlying html checkbox, we lost the ability to apply style to the checked status using checked: selector class
    // this feature is very powerful and I would like to preserve it. Unfortunately, there is no checked-within: selector, unlike focus state.
    // we can hack it by intercepting the classNames prop, and catching anything with checked selector.
    const classesWhenChecked = className
      ?.split(' ')
      ?.filter((cls) => /checked:/.test(cls))
      ?.map((cls) => cls.replace('checked:', ''))
      ?.join(' ')

    const classesWhenDisabled = className
      ?.split(' ')
      ?.filter((cls) => /disabled:/.test(cls))
      ?.map((cls) => cls.replace('disabled:', ''))
      ?.join(' ')

    const variants = {
      on: {
        x: '100%',
        scale: 0.85
      },
      off: {
        x: '0%',
        scale: 0.85
      },
      indeterminate: {
        x: '50%',
        scale: 0.85
      }
    }

    const animationState =
      status === 'loading' ? 'indeterminate' : value ? 'on' : 'off'

    const checkedIcon = useMemo(() => {
      if (props.checkedIcon && (props.checkedIcon as string) in IconType) {
        return <Icon name={props.checkedIcon as IconType} size={12} />
      }

      return props?.checkedIcon ?? null
    }, [props.checkedIcon])

    const uncheckedIcon = useMemo(() => {
      if (props.uncheckedIcon && (props.uncheckedIcon as string) in IconType) {
        return <Icon name={props.uncheckedIcon as IconType} size={12} />
      }

      return props?.uncheckedIcon ?? null
    }, [props.uncheckedIcon])

    return (
      <motion.div
        transition={{ duration: 0.2, type: 'tween', ease: 'easeOut' }}
        className={cn([
          'w-12 h-6 rounded-full transition-colors !cursor-pointer',
          className,
          value
            ? classesWhenChecked || 'bg-primary-0 text-primary-0'
            : 'bg-dark-3',
          disabled &&
            (classesWhenDisabled || 'cursor-not-allowed pointer-events-none')
        ])}
        {...motionProps}
        animate={animationState}
        onClick={() => {
          onChange(!value)
        }}
        onBlur={onBlur as FocusEventHandler<HTMLDivElement>}
      >
        <input
          type="checkbox"
          className="hidden"
          disabled={disabled}
          value={`${!!value}`}
          ref={ref}
          {...props}
        />
        <motion.div
          variants={variants}
          transition={{
            duration: 0.15,
            type: 'tween',
            ease: 'easeOut',
            when: 'afterChildren'
          }}
          className="flex justify-center items-center m-0 rounded-full h-full w-1/2 bg-white leading-none font-medium"
        >
          <AnimatePresence>
            {status === 'loading' ? (
              <Icon name="loading" size={12} />
            ) : disabled ? (
              <Icon name="lock-outline" size={12} className="text-dark-2" />
            ) : (
              <>
                {checkedIcon && value && <>{checkedIcon}</>}
                {uncheckedIcon && !value && <>{uncheckedIcon}</>}
              </>
            )}
          </AnimatePresence>
        </motion.div>
      </motion.div>
    )
  }
)
// Important for Field & SmartField component integration!
Switch.displayName = 'Switch'
export default Switch
